import { NotificationType } from "../Data/ApiTransport";

export const RoleList = {
    SuperAdmin: {
        identity:"SUPERADMIN",
        name: 'SuperAdmin'
    },
    OperatorAdmin: {
        identity:"OPERATORADMIN",
        name: 'OperatorAdmin'
    },
    FacilityUser: {
        identity:"FACILITYUSER",
        name:'FacilityUser'
    },
    TransientReviewer: {
        identity:"TRANSIENTREVIEWER",
        name:'TransientReviewer'
    }
}

export const app_roles = [
    {text:'SuperAdmin', value: 0, identity: RoleList.SuperAdmin.identity},
    {text:'OperatorAdmin', value:1, identity: RoleList.OperatorAdmin.identity}, 
    {text:'FacilityUser',value:2,identity: RoleList.FacilityUser.identity},
    {text:'TransientReviewer',value:3,identity: RoleList.TransientReviewer.identity}];
export function isAdminRole(currentUserRoles: string[]): boolean {
    return currentUserRoles.includes('SuperAdmin') || currentUserRoles.includes('OperatorAdmin') ;
}

// Role identity helpers (identities are uppercase strings)
export function isAdminIdentity(roleIdentity: string | undefined): boolean {
    if (!roleIdentity) return false;
    const id = roleIdentity.toUpperCase();
    return ADMIN_IDENTITIES.includes(id);
}

// Determines whether a given role identity requires explicit facility assignments
// Default policy: all non-admin roles must have facilities explicitly assigned
export function requiresFacilityAssignment(roleIdentity: string | undefined): boolean {
    if (!roleIdentity) return false;
    if (isAdminIdentity(roleIdentity)) return false;
    const id = roleIdentity.toUpperCase();
    // If configured list provided, only those identities require assignment
    if (FACILITY_SCOPED_IDENTITIES.length > 0) {
        return FACILITY_SCOPED_IDENTITIES.includes(id);
    }
    // Fallback: treat any other non-admin role as facility-scoped
    return true;
}

// Configuration: edit these arrays to customize behavior
// Admin identities have access to everything automatically
export const ADMIN_IDENTITIES: string[] = [
    RoleList.SuperAdmin.identity,
    RoleList.OperatorAdmin.identity
];

// Identities that must have specific facilities assigned.
// If this list is left empty, the fallback is: all non-admin roles require facility assignment.
export const FACILITY_SCOPED_IDENTITIES: string[] = [
    RoleList.FacilityUser.identity,
    RoleList.TransientReviewer.identity
];

// Optional: Notification opt-in visibility per role identity.
// If a role identity is configured here, ONLY the listed notification types
// will be visible to users with that role (admins still see all).
// If a role identity is NOT present here, the default is: show all notification types.
export const ROLE_NOTIFICATION_OPT_INS: Record<string, NotificationType[]> = {
    [RoleList.TransientReviewer.identity]: [NotificationType.TransientParkerPaid]
};

export function userCanSeeNotificationOptIn(currentUserRoles: string[] | undefined, notificationType: NotificationType): boolean {
    if (!currentUserRoles || currentUserRoles.length === 0) return false;
    if (isAdminRole(currentUserRoles)) return true;
    // For non-admins: if ANY of the user's roles is configured and includes this notification, allow it.
    // If none of the user's roles are configured, default to showing all.
    let anyRoleConfigured = false;
    for (const roleName of currentUserRoles) {
        const identity = (RoleList as any)[roleName]?.identity ?? roleName.toUpperCase();
        const allowed = ROLE_NOTIFICATION_OPT_INS[identity];
        if (allowed) {
            anyRoleConfigured = true;
            if (allowed.includes(notificationType)) return true;
        }
    }
    return anyRoleConfigured ? false : true;
}

// Facility tab identifiers used by `AppFacility` sidebar
export type FacilityTabKey =
    | 'overview'
    | 'listing'
    | 'status'   // Transient
    | 'parker'   // Monthly
    | 'plan'
    | 'rate'
    | 'finance'
    | 'reports'

// Centralized, scalable permissions map for Facility tabs
// Only list roles that should have access; admins are automatically allowed via `isAdminRole`
export const facilityTabPermissions: Record<FacilityTabKey, string[]> = {
    overview: [RoleList.FacilityUser.name],
    listing: [RoleList.FacilityUser.name],
    status: [RoleList.TransientReviewer.name, RoleList.FacilityUser.name],
    parker: [RoleList.FacilityUser.name],
    plan: [RoleList.FacilityUser.name],
    rate: [RoleList.FacilityUser.name],
    finance: [RoleList.FacilityUser.name],
    reports: [RoleList.FacilityUser.name],
};

export function userCanAccessFacilityTab(currentUserRoles: string[] | undefined, tab: FacilityTabKey): boolean {
    if (!currentUserRoles || currentUserRoles.length === 0) return false;
    if (isAdminRole(currentUserRoles)) return true;
    const allowed = facilityTabPermissions[tab] ?? [];
    return allowed.some(r => currentUserRoles.includes(r));
}

export function getFirstPermittedFacilityTab(currentUserRoles: string[] | undefined): FacilityTabKey | undefined {
    const order: FacilityTabKey[] = ['overview','listing','status','parker','plan','rate','finance','reports'];
    for (const key of order) {
        if (userCanAccessFacilityTab(currentUserRoles, key)) return key;
    }
    return undefined;
}